import api, { ApiResponse } from './api';

export type SiteTransferStatus = 'PENDING' | 'ACCEPTED' | 'REJECTED' | 'CANCELLED' | 'EXPIRED';

export interface SiteTransfer {
  id: string;
  siteId: string;
  siteName: string;
  fromOrganizationName: string;
  recipientContact: string;
  status: SiteTransferStatus;
  expiresAt: string;
  createdAt: string;
  toOrganizationName?: string;
  acceptedAt?: string;
}

export interface SiteTransferInfo {
  id: string;
  siteName: string;
  siteAddress: string;
  /** 요청 조직 이름 — 서버 `getTransferByToken`의 키(2026-05-31 `7044dc65a` rename 뒤 web이 옛 키를 읽어 빈 이름이 보였다) */
  fromCompanyName: string;
  requestedByUserId: string;
  inspectionCount: number;
  reportCount: number;
  expiresAt: string;
}

export interface RequestSiteTransferDto {
  recipientContact: string;
}

export interface TransferSiteToMyOrgDto {
  toOrganizationId: string;
  toGroupId?: string;
}

export interface AcceptTransferDto {
  toOrganizationId: string;
  toGroupId?: string;
}

/**
 * 이관 요청 (다른 Owner에게 이메일 발송)
 * POST /api/v1/organizations/:orgId/sites/:siteId/transfer
 */
export async function requestSiteTransfer(
  orgId: string,
  siteId: string,
  dto: RequestSiteTransferDto
): Promise<SiteTransfer> {
  const response = await api.post<ApiResponse<SiteTransfer>>(
    `/organizations/${orgId}/sites/${siteId}/transfer`,
    dto
  );
  if (!response.data.data) throw new Error('이관 요청 실패');
  return response.data.data;
}

/**
 * 내 다른 조직으로 즉시 이동
 * POST /api/v1/organizations/:orgId/sites/:siteId/transfer/move
 */
export async function transferSiteToMyOrg(
  orgId: string,
  siteId: string,
  dto: TransferSiteToMyOrgDto
): Promise<void> {
  await api.post(`/organizations/${orgId}/sites/${siteId}/transfer/move`, dto);
}

/**
 * 토큰으로 이관 정보 조회 (공개)
 * GET /api/v1/site-transfers/token/:token
 */
export async function getTransferByToken(token: string): Promise<SiteTransferInfo> {
  const response = await api.get<ApiResponse<SiteTransferInfo>>(`/site-transfers/token/${token}`, {
    skipAuth: true,
  } as Parameters<typeof api.get>[1] & { skipAuth: boolean });
  if (!response.data.data) throw new Error('이관 정보를 찾을 수 없습니다.');
  return response.data.data;
}

/**
 * 이관 수락
 * POST /api/v1/site-transfers/token/:token/accept
 */
export async function acceptTransfer(token: string, dto: AcceptTransferDto): Promise<void> {
  await api.post(`/site-transfers/token/${token}/accept`, dto);
}

/**
 * 이관 거부
 * POST /api/v1/site-transfers/token/:token/reject
 */
export async function rejectTransfer(token: string): Promise<void> {
  await api.post(`/site-transfers/token/${token}/reject`);
}

/**
 * 이관 취소 (보내는 쪽 Owner)
 * POST /api/v1/site-transfers/:transferId/cancel
 */
export async function cancelTransfer(transferId: string): Promise<void> {
  await api.post(`/site-transfers/${transferId}/cancel`);
}

/**
 * 이관 목록 조회 (현재 조직이 보낸 이관 요청)
 * GET /api/v1/organizations/:orgId/site-transfers
 */
export async function getTransfers(orgId: string): Promise<SiteTransfer[]> {
  const response = await api.get<ApiResponse<SiteTransfer[]>>(
    `/organizations/${orgId}/site-transfers`
  );
  return response.data.data ?? [];
}
